import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import { Map } from 'immutable';
import Actions from 'flow/actions';
import { getBrand } from 'flow/brands/brands';
import { electronActions, electronListeners } from 'flow/utils/electron-utils';
import {
    signIn,
    clearLoginData,
    setPreventUnload,
    noPassword,
    checkVersion,
    updateLangConstants,
    updateApplicationState,
    setAutoLoginWithAccessToken,
} from 'flow/actions/app-actions';
import { unregisterTopConnection } from 'flow/actions/top-actions';
import {
    startExternalSignIn,
    cancelExternalSignIn,
    startTeamsMicrosoftSignIn,
} from 'flow/actions/social-integration-actions';
import SignInForm from 'flow/components/SignInForm';
import {
    getTelavoxFormattedLanguageCode,
    isEmptyNullOrUndefined,
} from 'flow/utils';
import {
    getApplicationLanguage,
    getAutoLogin,
    setAutoLogin,
} from 'flow/utils/browser-utils';
import { createReturnUrl } from 'flow/utils/social-integration-utils';
import { getAllowedVersion } from 'flow/selectors/selectors';
import Loading from 'flow/components/ui-components/Loading';
import AppContext from 'flow/contexts/app-context';
import { hasTooManyInstances } from 'flow/selectors/cti-integration-selectors';
import {
    cancelCtiExternalSignIn,
    startCtiGoogleSignIn,
    startCtiMicrosoftSignIn,
    startCtiOidcSignIn,
} from 'flow/route-components/cti-sso';
import { isEmbeddedTypeCti, isTeamsFullClient } from 'flow/utils/app-utils';
import { MSGOO_APP_REG_DEFAULT_VALUE } from 'flow/utils/social-integration-utils';
import { SYNC_SOURCE } from 'flow/utils/constants/app-constants';
import isElectron from 'is-electron';
import appConfig from 'app-config';
import { isUaePassEnabled } from 'flow/constants/uae-pass/uae-pass-constants';
import { revealDesktopWindow } from 'flow/companion/companion-window';

/**
 * NOTE: This component is intended to be used as the login page for Electron
 * To sign in from the web, use the https://${getBrand().brandAdminUrl}/flowin.jsp page
 */
class SignIn extends React.PureComponent {
    static propTypes = {
        dispatch: PropTypes.func.isRequired,
        loginData: PropTypes.instanceOf(Map),
        externalSigninData: PropTypes.instanceOf(Map),
        allowedVersion: PropTypes.bool.isRequired,
        tooManyInstances: PropTypes.bool.isRequired,
    };

    static contextType = AppContext;

    constructor(props, context) {
        super(props);

        this.state = {
            username: '',
            password: '',
            savedUsername: null,
            showOtherSignInForm: false,
            enabledOidcSignIn: true,
            usernameOther: '',
            userLanguage:
                getApplicationLanguage() ||
                getTelavoxFormattedLanguageCode(context.i18next.language) ||
                'en',
            autoSigningIn: false,
            isLoginLoading: true,
        };

        this.hasRevealedForSignIn = false;

        updateLangConstants(this.state.userLanguage);

        // Check version if NO_UPDATE
        if (process.env.NO_UPDATE === 'true') {
            this.props.dispatch(checkVersion());
        }

        if (!isElectron()) {
            this.setState({ isLoginLoading: false });
        }
    }

    async onAutoLoginProcess() {
        try {
            /**
             * Legacy credential-based auto-login (backward compatibility).
             * Preserved so existing users with saved passwords are not disrupted.
             * On sign-out, stored credentials are cleaned up and replaced by the
             * new Integration Access Token approach going forward.
             */
            const credentials = await electronActions.getUserCredentials();

            if (credentials) {
                const username = credentials.get('username');
                const password = credentials.get('password');

                const rememberCredentials =
                    !isEmptyNullOrUndefined(username) ||
                    !isEmptyNullOrUndefined(password);
                this.setState({
                    password,
                    username,
                    savedUsername: username,
                });

                if (getAutoLogin() && rememberCredentials) {
                    this.signIn(username, password);
                    this.setState({
                        autoSigningIn: true,
                        isLoginLoading: false,
                    });
                } else {
                    this.setState({
                        autoSigningIn: false,
                        isLoginLoading: false,
                    });
                }
            } else {
                const { savedIAToken, savedIATokenId } =
                    (await electronActions.getIntegrationToken()) || {};

                // Signing out turns auto-login off; a token still stored after that is stale.
                if (savedIAToken && !getAutoLogin()) {
                    await electronActions.deleteIntegrationToken();
                    this.setState({ isLoginLoading: false });
                    return;
                }

                this.props.dispatch(
                    setAutoLoginWithAccessToken(savedIATokenId),
                );

                if (savedIAToken) {
                    this.signInWithToken(savedIAToken);
                } else {
                    this.setState({ isLoginLoading: false });
                }
            }
        } catch (error) {
            console.error('Auto-login process failed:', error);
            this.setState({ isLoginLoading: false, autoSigningIn: false });
        }
    }

    async componentDidMount() {
        this.props.dispatch(unregisterTopConnection);
        this.props.dispatch(clearLoginData);

        if (!appConfig.dev && isElectron() && !isUaePassEnabled) {
            await this.onAutoLoginProcess();
        } else {
            this.setState({ isLoginLoading: false });
        }
    }

    componentDidUpdate() {
        this.props.dispatch(unregisterTopConnection);
        this.revealWindowWhenSignInNeedsAPerson();
    }

    /**
     * Reaching the sign-in form means a hidden companion cannot look after itself - no saved
     * credentials, "remember me" off, an expired token, a refusal - and would otherwise sit there
     * running, signed out and invisible.
     *
     * Hung off the same condition as the render, so it cannot disagree with what is on screen.
     * Once only, so signing out and back in does not keep pulling the window forward.
     */
    revealWindowWhenSignInNeedsAPerson = () => {
        if (this.hasRevealedForSignIn) {
            return;
        }
        const { autoSigningIn, isLoginLoading } = this.state;
        if (autoSigningIn || isLoginLoading) {
            return;
        }
        this.hasRevealedForSignIn = true;
        revealDesktopWindow('sign-in needs the user');
    };

    onSuccessfulSignIn = (username, password) => {
        this.setState({ isLoginLoading: false });

        if (isElectron()) {
            setAutoLogin(true);
            electronActions.saveUserCredentials(username, password);
        } else {
            setAutoLogin(false);
        }
    };

    onUsernameChanged = (username) => {
        const { savedUsername } = this.state;
        if (savedUsername && username !== savedUsername) {
            electronActions.deleteUserCredentials();
            setAutoLogin(false);
            this.setState({ username, password: '', savedUsername: null });
        } else {
            this.setState({ username });
        }
    };

    onPasswordChanged = (password) => {
        this.setState({ password });
    };

    onUsernameOtherChanged = (usernameOther) => {
        this.setState({ usernameOther });
    };

    setShowOtherSignInForm = (showOtherSignInForm) => {
        this.setState({ showOtherSignInForm });
    };

    setEnabledOidcSignIn = (enabledOidcSignIn) => {
        this.setState({ enabledOidcSignIn });
    };

    onSignInError = (error) => {
        if (error.loginFail) {
            setAutoLogin(false);
            if (error?.accessToken) {
                electronActions.deleteIntegrationToken();
            } else {
                electronActions.deleteSavedUserName();
            }
        }
        this.setState({ autoSigningIn: false, isLoginLoading: false });
        this.resetPasswordOnError(error);
    };

    resetPasswordOnError = (error) => {
        if (error && error.loginFail) {
            this.setState({ password: '' });
        }
    };

    signInWithToken = (accessToken) => {
        const { dispatch } = this.props;
        if (accessToken) {
            dispatch(
                signIn(
                    null,
                    null,
                    () => {
                        setAutoLogin(true);
                        this.setState({ isLoginLoading: false });
                    },
                    this.onSignInError,
                    accessToken,
                ),
            );
        } else {
            electronActions.deleteIntegrationToken();
        }
    };

    signIn = (username, password) => {
        const { dispatch } = this.props;
        if (password) {
            dispatch(
                signIn(
                    username,
                    password,
                    () => {
                        dispatch(updateApplicationState('boot'));
                        return this.onSuccessfulSignIn(username, password);
                    },
                    this.onSignInError,
                ),
            );
        } else {
            dispatch(noPassword());
        }
    };

    handleSignIn = (username, password) => {
        this.signIn(username, password);
    };

    startGoogleSignIn = () => {
        this.startExternalSignIn(
            SYNC_SOURCE.google,
            MSGOO_APP_REG_DEFAULT_VALUE,
        );
    };

    startMicrosoftSignIn = () => {
        this.startExternalSignIn(
            SYNC_SOURCE.microsoft,
            MSGOO_APP_REG_DEFAULT_VALUE,
        );
    };

    startTeamsMicrosoftSignIn = () => {
        this.props.dispatch(startTeamsMicrosoftSignIn());
    };

    getMicrosoftSignIn = (isTeamsEmbedded) =>
        isTeamsEmbedded
            ? this.startTeamsMicrosoftSignIn
            : this.startMicrosoftSignIn;

    startOidcSignIn = (appRegistrationId) => {
        this.startExternalSignIn(SYNC_SOURCE.oidc, appRegistrationId);
    };

    startExternalSignIn = (syncSource, appRegId) => {
        const { dispatch } = this.props;
        electronListeners.checkIfExternalSignInFinished(dispatch);
        const callbackUrl = createReturnUrl('dummy', syncSource);
        dispatch(startExternalSignIn(syncSource, callbackUrl, appRegId));
    };

    cancelExternalSignIn = () => {
        const { dispatch } = this.props;
        dispatch(cancelExternalSignIn());
    };

    receiveExternalSigninData = (data) => ({
        type: Actions.APP_EXTERNAL_SIGNIN_DATA,
        data: data,
    });

    onLinkClick = () => {
        setPreventUnload(true);
    };

    onLanguageChange = (languageCode) => {
        const adjustedLanguageCode =
            getTelavoxFormattedLanguageCode(languageCode);

        this.setState({
            userLanguage: updateLangConstants(adjustedLanguageCode),
        });
    };

    getPasswordForgottenUrl = () => {
        const passwordActionUrl = getBrand().passwordActionUrl;
        if (!passwordActionUrl) {
            return null; // Return null when password reset is not available
        }
        return `${passwordActionUrl}/signin.jsp?pwreset`;
    };

    render() {
        const {
            loginData,
            externalSigninData,
            allowedVersion,
            tooManyInstances,
        } = this.props;
        const {
            username,
            password,
            showOtherSignInForm,
            enabledOidcSignIn,
            usernameOther,
            userLanguage,
            autoSigningIn,
            isLoginLoading,
        } = this.state;
        const isCtiEmbedded = isEmbeddedTypeCti(this.context.embedded);
        const isTeamsEmbedded = isTeamsFullClient(this.context.embedded);

        if (autoSigningIn || isLoginLoading) {
            return <Loading />;
        }

        const signInProps = {
            password,
            username,
            usernameOther,
            onUsernameChanged: this.onUsernameChanged,
            onPasswordChanged: this.onPasswordChanged,
            onUsernameOtherChanged: this.onUsernameOtherChanged,
            setShowOtherSignInForm: this.setShowOtherSignInForm,
            setEnabledOidcSignIn: this.setEnabledOidcSignIn,
            handleSignIn: this.handleSignIn,
            loginData,
            externalSigninData,
            onLinkClick: this.onLinkClick,
            onLanguageChange: this.onLanguageChange,
            userLanguage,
            passwordUrl: this.getPasswordForgottenUrl(),
            allowedVersion,
            showOtherSignInForm,
            enabledOidcSignIn,
            tooManyInstances,
            isCtiEmbedded,
            isTeamsEmbedded,
            startGoogleSignIn: isCtiEmbedded
                ? startCtiGoogleSignIn
                : this.startGoogleSignIn,
            startMicrosoftSignIn: isCtiEmbedded
                ? startCtiMicrosoftSignIn
                : this.getMicrosoftSignIn(isTeamsEmbedded),
            startOidcSignIn: isCtiEmbedded
                ? startCtiOidcSignIn
                : this.startOidcSignIn,
            cancelExternalSignIn: isCtiEmbedded
                ? cancelCtiExternalSignIn
                : this.cancelExternalSignIn,
        };

        return <SignInForm {...signInProps} />;
    }
}

const mapStateToProps = (state) => {
    const { app } = state;
    const loginData = app.get('loginData', Map());
    const externalSigninData = app.get('externalSigninData', Map());
    const allowedVersion = getAllowedVersion(state);
    const tooManyInstances = hasTooManyInstances(state);

    return {
        loginData,
        externalSigninData,
        allowedVersion,
        tooManyInstances,
    };
};

export default connect(mapStateToProps)(SignIn);
